/**
 * CertificationSummary - WBC3 Vessel Certification Component
 * Determines primary certificate type and generates vessel number based on RA verification
 */

import React from 'react';
import { Award, FileCheck, Info, AlertCircle } from 'lucide-react';
import WizardSection from '../../WizardSection';

// Primary certificate determination logic (RA-verified)
const determinePrimaryCertificate = (draftConfig) => {
  const vesselType = draftConfig.vesselType || draftConfig.type;
  const operations = draftConfig.operations || [];

  // Police boat certificate (WBC3 Annex 3)
  if (vesselType === 'police' || operations.includes('patrol_operations')) {
    return {
      type: 'police_boat',
      name: 'Police Boat Certificate', 
      code: 'PO',
      description: 'Special certificate for police/enforcement vessels',
      reference: 'WBC3 Annex 3',
      color: 'purple',
      validity: '5 years',
      notes: 'Enhanced LSA training required for crew (Annex 3)'
    };
  }

  // Pilot boat certificate (WBC3 Section 27) 
  if (vesselType === 'pilot' || operations.includes('pilot_transfer')) {
    return {
      type: 'pilot_boat',
      name: 'Pilot Boat Certificate',
      code: 'PB', 
      description: 'Special certificate for pilot transfer operations',
      reference: 'WBC3 Section 27',
      color: 'blue',
      validity: '5 years',
      notes: 'Special LSA equipment required (Table 27.2.3)'
    };
  }

  // Standard workboat certificate (default)
  return {
    type: 'workboat',
    name: 'Workboat Certificate',
    code: 'WB',
    description: 'Standard commercial workboat certificate', 
    reference: 'WBC3 Section 3',
    color: 'gray',
    validity: '5 years',
    notes: 'Standard WBC3 requirements apply'
  };
};

// Additional certificates based on operations
const getAdditionalCertificates = (draftConfig) => {
  const additional = [];
  const vesselType = draftConfig.vesselType || draftConfig.type;
  const operations = draftConfig.operations || [];

  // Pilot boat endorsement (can be added to workboat cert)
  if (operations.includes('pilot_transfer') && vesselType !== 'pilot') {
    additional.push({
      name: 'Pilot Boat Endorsement',
      description: 'Workboat Certificate with Pilot Boat Endorsement',
      reference: 'WBC3 Section 3.9',
      reason: 'Selected pilot transfer operations'
    });
  }

  // Light duty workboat certificate option
  if (draftConfig.category >= 4) {
    additional.push({
      name: 'Light Duty Workboat Certificate',
      description: 'Alternative certificate for Category 4+ vessels',
      reference: 'WBC3 Section 3.9.3',
      reason: 'Available for Cat 4+ vessels as alternative',
      optional: true
    });
  }

  // Dangerous goods certificates
  if (operations.includes('dangerous_goods')) {
    additional.push({
      name: 'DoC DG Certificate',
      description: 'Declaration of Compliance for dangerous goods',
      reference: 'IMDG Code + WBC3 Section 29',
      reason: 'Required for dangerous goods operations'
    });
  }

  return additional;
};

// Generate vessel number based on certificate type and vessel dimensions
const generateVesselNumber = (certificate, draftConfig) => {
  const year = new Date().getFullYear().toString().slice(-2);
  const code = certificate.code || 'WB';
  const length = Math.floor(parseFloat(draftConfig.lengthOverall) || 0).toString().padStart(3, '0');
  return `UK-${code}-${year}-${length}-XXXX`;
};

const CertificationSummary = ({ draftConfig }) => {
  const primaryCertificate = determinePrimaryCertificate(draftConfig);
  const additionalCertificates = getAdditionalCertificates(draftConfig);
  const vesselNumber = generateVesselNumber(primaryCertificate, draftConfig);

  return (
    <WizardSection.Card>
      <WizardSection.Header>Vessel Certification Summary</WizardSection.Header>
      <WizardSection.Text>
        Based on your vessel configuration, the following certification requirements apply.
      </WizardSection.Text>

      {/* Primary Certificate */}
      <div className="mt-6">
        <h4 className="text-base font-medium text-gray-900 mb-4">Primary Vessel Certificate</h4>
        <div className={`border rounded-lg p-4 bg-${primaryCertificate.color}-50 border-${primaryCertificate.color}-200`}>
          <div className="flex items-start gap-3">
            <Award className={`h-6 w-6 flex-shrink-0 mt-0.5 text-${primaryCertificate.color}-600`} />
            <div className="flex-1">
              <h5 className="text-lg font-medium text-gray-900">{primaryCertificate.name}</h5>
              <p className="text-sm text-gray-600 mt-1">{primaryCertificate.description}</p>
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">
                    <strong>Certificate Code:</strong> {primaryCertificate.code}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">
                    <strong>Validity:</strong> {primaryCertificate.validity}
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-gray-500 mt-0.5" />
                  <span className="text-sm text-gray-700">
                    <strong>Reference:</strong> {primaryCertificate.reference}
                  </span>
                </div>
              </div>
              <WizardSection.Callout variant="info" title="Certificate Requirements">
                {primaryCertificate.notes}
              </WizardSection.Callout>
            </div>
          </div>
        </div>
      </div>

      {/* Vessel Number Generation */}
      <div className="mt-6">
        <h4 className="text-base font-medium text-gray-900 mb-3">Proposed Vessel Number</h4>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-lg font-mono font-medium text-gray-900">{vesselNumber}</span>
            <span className="text-sm text-gray-500">Format: UK-{primaryCertificate.code}-YY-LOA-XXXX</span>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Based on {primaryCertificate.code} certificate code and {draftConfig.lengthOverall || 'unknown'}m length overall.
            Final four digits (XXXX) will be assigned by MCA.
          </p>
        </div>
      </div>

      {/* Additional Certificates */}
      {additionalCertificates.length > 0 && (
        <div className="mt-6">
          <h4 className="text-base font-medium text-gray-900 mb-3">Additional Certificates</h4>
          <div className="space-y-3">
            {additionalCertificates.map((cert, index) => (
              <div key={index} className={`border rounded-lg p-4 ${
                cert.optional 
                  ? 'bg-blue-50 border-blue-200' 
                  : 'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h5 className="text-sm font-medium text-gray-900">{cert.name}</h5>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        cert.optional 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {cert.optional ? 'Optional' : 'Required'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{cert.description}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      <strong>Reason:</strong> {cert.reason}
                    </p>
                    <p className="text-xs text-cyan-600 mt-1">{cert.reference}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <WizardSection.Callout variant="regulatory" title="Certification Process">
        All certificates must be obtained from the Maritime and Coastguard Agency (MCA) 
        following vessel survey and compliance verification. Certificate validity is typically 5 years 
        with annual endorsements required.
      </WizardSection.Callout>
    </WizardSection.Card>
  );
};

export default CertificationSummary;